/**
 * ============================================================================
 * FILTER BAR — property filter + actions for the Executive Summary page.
 * ============================================================================
 *
 * One filter system across the app: the same Cloudscape PropertyFilter and
 * Redux `filterQuery` used by the WBR home page and the KPI tiles page, so
 * filter selections persist across pages (QuickSight sheets behave the same
 * way). Tokens are applied as row-level DCR predicates before aggregation —
 * see src/features/api/filterPredicate.ts.
 *
 * Filter changes NEVER trigger an API call; they recompute locally. Only the
 * week-window controls (WeekControls) change the fetch.
 *
 * PRESETS: Save Preset snapshots the current query to localStorage
 * keyed `wbr-presets:default` (user alias once auth lands).
 */

import React, { useState } from 'react';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import Button from '@amzn/awsui-components-core/button';
import PropertyFilter from '@amzn/awsui-components-core/property-filter';
import { useAppDispatch, useAppSelector } from 'src/app/store';
import { filterQueryChanged, FilterQuery } from 'src/features/dashboard/dashboardSlice';
import { FILTER_PROPERTIES, DEFAULT_FILTER_QUERY } from 'src/config/filterConfig';
import { useFilterOptions } from 'src/components/filters/useFilterOptions';
import { SavePresetModal } from './SavePresetModal';

/** localStorage key for saved presets (user alias once auth is wired). */
const PRESETS_STORAGE_KEY = 'wbr-presets:default';

interface FilterPreset {
  name: string;
  query: FilterQuery;
  savedAt: string;
}

export const FilterBar: React.FC = () => {
  const dispatch = useAppDispatch();
  const query = useAppSelector((state) => state.dashboard.filterQuery);
  const [presetModalVisible, setPresetModalVisible] = useState(false);
  // QuickSight parity: dimension dropdown values come from the fetched data,
  // never from a hardcoded list (static lists only for Report Period etc.).
  const filteringOptions = useFilterOptions();

  const handleSavePreset = (name: string) => {
    // Duplicate names overwrite; corrupt/absent storage falls back to [].
    let presets: FilterPreset[] = [];
    try {
      presets = JSON.parse(localStorage.getItem(PRESETS_STORAGE_KEY) || '[]');
    } catch {
      presets = [];
    }
    const preset: FilterPreset = { name, query, savedAt: new Date().toISOString() };
    const next = [...presets.filter((p) => p.name !== name), preset];
    try {
      localStorage.setItem(PRESETS_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Quota exceeded — keep only the most recent presets and retry once.
      try {
        localStorage.setItem(PRESETS_STORAGE_KEY, JSON.stringify(next.slice(-5)));
      } catch {
        /* preset saving is best-effort */
      }
    }
    setPresetModalVisible(false);
  };

  return (
    <SpaceBetween direction="vertical" size="s">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '8px',
        }}
      >
        <div style={{ flexGrow: 1, minWidth: '480px' }}>
          <PropertyFilter
            query={query}
            onChange={({ detail }) => dispatch(filterQueryChanged(detail as FilterQuery))}
            filteringProperties={FILTER_PROPERTIES}
            filteringOptions={filteringOptions}
            disableFreeTextFiltering={true}
            i18nStrings={{
              filteringAriaLabel: 'Filter metrics',
              filteringPlaceholder: 'Search or select a filter',
              groupValuesText: 'Values',
              groupPropertiesText: 'Properties',
              operatorsText: 'Operators',
              operationAndText: 'and',
              operationOrText: 'or',
              clearFiltersText: 'Clear filters',
              removeTokenButtonAriaLabel: (token) =>
                `Remove filter ${token.propertyKey} ${token.operator} ${token.value}`,
              enteredTextLabel: (text) => `Use: "${text}"`,
            }}
          />
        </div>

        {/* No custom "Clear filters" button — PropertyFilter renders its own
            built-in clear link when tokens exist (clearFiltersText above). */}
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            variant="normal"
            onClick={() => dispatch(filterQueryChanged(DEFAULT_FILTER_QUERY))}
          >
            Reset to defaults
          </Button>
          <Button variant="primary" onClick={() => setPresetModalVisible(true)}>
            Save Preset
          </Button>
        </SpaceBetween>
      </div>

      <SavePresetModal
        visible={presetModalVisible}
        onDismiss={() => setPresetModalVisible(false)}
        onSave={handleSavePreset}
      />
    </SpaceBetween>
  );
};
